import { createContext, useContext, useMemo } from "react";
import { suspensify } from "lib/utils/suspensify";
import { useCurrentConsumerProject } from "./current-consumer-project-context";
import { zrpc } from "../zrpc";

function useConsumerResource() {
  const project = useCurrentConsumerProject();
  const { refetch } = zrpc.interfaces.useQuery(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}/consumers/me",
    { params: { path: { interface_id: project.id } } },
    {
      enabled: false,
    }
  );

  return useMemo(
    () =>
      suspensify(
        Promise.resolve(project).then((proj) => {
          if (proj.authType !== "Consumer") {
            return undefined;
          }

          return refetch().then((result) =>
            result.isSuccess ? result.data : undefined
          );
        })
      ),
    [project, refetch]
  );
}

const ConsumerContext = createContext<ReturnType<typeof useConsumerResource>>({
  read() {
    throw new Promise(() => {
      // infinite suspense
    });
  },
});

export function ConsumerProvider({ children }: { children: React.ReactNode }) {
  const consumerResource = useConsumerResource();

  return (
    <ConsumerContext.Provider value={consumerResource}>
      {children}
    </ConsumerContext.Provider>
  );
}

export function useConsumer() {
  const consumerResource = useContext(ConsumerContext);

  return consumerResource.read();
}
